import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { AxiosUtils } from '../../shared/utils';
import { LoanApplicationTask } from '../../shared/models';
import { CerebroTextButton } from '../../shared/components';
import { CerebroApiEndpoints } from '../../shared/utils/api';

export const MarkDocumentAsCompleteDialog: React.FC<MarkDocumentAsCompleteDialogProps> = ({
	open = false,
	onClose,
	document: requiredDocument,
}) => {
	const api = useApi();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const mutation = useMutation<LoanApplicationTask, unknown, LoanApplicationTask>(
		document => {
			return AxiosUtils.getResponseData<LoanApplicationTask>(
				api.patch(CerebroApiEndpoints.RequiredDocuments.Specific(guid, document?.guid ?? ''), {
					updateType: 'Completed',
					completed: true,
					notApplicable: false,
					uploads: [],
				})
			);
		},
		{
			onSuccess: (_data, document) => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid));
				snackbar.enqueueSnackbar(`Successfully marked '${document.applicationTaskTitle}' as Complete.`, {
					variant: 'success',
				});
			},
			onError: () => {
				snackbar.enqueueSnackbar('Something went wrong when clearing the document as N/A.', {
					variant: 'error',
				});
			},
			onSettled: onClose,
		}
	);

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle>Confirm Action</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to mark &lsquo;{requiredDocument.applicationTaskTitle}&rsquo; as complete? You
					will not be able to upload any more files.
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={onClose}>No</CerebroTextButton>
				<CerebroTextButton onClick={() => mutation.mutate(requiredDocument)}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};

interface MarkDocumentAsCompleteDialogProps {
	open?: boolean;
	onClose: () => void;
	document: LoanApplicationTask;
}
